import React, { useEffect, useRef, useState, useMemo } from 'react';
import Icon from '../icon/Icon';
import './select.scss';
import { setAllModels, setStories } from '@/stores/slices/modelsState';
import Input from '../input/Input';
import { useDispatch, useSelector } from 'react-redux';
import { resetPage } from '@/stores/slices/modelsState';
import { useLocation, useParams } from 'react-router-dom';
import {
	setCity,
	setCurrentCity,
	setUserCurrentDistrict,
	getUserCurrentDistrict,
	setTempCatalogueCity,
} from '@/stores/slices/userSlice';
import { clearTitle } from '@/stores/slices/titleSlice';
import useCityChange from '@/hooks/useCityChange';
import { getUserLang } from '@/stores/slices/userSlice';
import { showFilter } from '@/stores/slices/showFilterSlice';
import { setProfileCount } from '@/stores/slices/mapModelSlice';
import useLadyService from '@/services/LadyService';
import {
	getCountriesData,
	setCountry,
	setCountrySlug,
} from '../../../stores/slices/userSlice';
import { Dropdown } from '@/components/ui';
import { useFilterLogicContext } from '@/components/filter/FilterLogicProvider';
import { CityChangeSaveParams } from '@/components/filter/FilterData';
import { setSortParam } from '@/stores/slices/filterSlice';

const SelectCountry = ({ ...props }) => {
	const {
		arrowSize,
		clazz,
		clazzSvg,
		clazzWrapper,
		name,
		setValue = undefined,
		onChange = false,
		paramsOnChange = false,
		defaultTitle = '',
		withoutIcon = false,
		withoutTitle,
		withoutArrow,
		setActive,
		setIsAddressActive,
		data,
		activeCountry,
		setActiveCountry = false,
		error,
		errorMessage,
		setError,
		isMain = false,
		open = false,
		disabled = false,
		placeholder,
		required,
		isAgencyFilter = false,
		selectCountry = true,
		changeParty = false,
		setWholeObjectValue,
		selectDistrict = false,
		deferApply = false, // When true, skip Redux dispatches; notify parent via onSelect
		onSelect = false,
	} = props;

	// `data` is an array of objects, each holding one country:
	// [{ cz: {...} }, { sk: {...} }]  (a single object with several
	// country keys also works). Flatten ALL of them — looking at
	// data[0] only would make every country after the first unfindable.
	const countryEntries = data?.length
		? data.flatMap((item) => Object.entries(item))
		: [];

	const transformedArray = countryEntries.map(([code, { name, profiles_count }]) => ({
		code,
		name,
		profiles_count
	}));

	const { dispatch, t, scrollToTop } = useLadyService();
	const currentDistrict = useSelector(getUserCurrentDistrict);
	const { params: url_params } = useParams();

	const FILTER = useFilterLogicContext();

	const changeCity = useCityChange();
	const lang = useSelector(getUserLang);
	const isFilterOpen = useSelector(showFilter);

	/**
	 * Resolve an `activeCountry` value (string code/slug, country object,
	 * or an array of country objects) to the lowercase country key
	 * used in `data` (e.g. 'cz', 'sk').
	 */
	const resolveCountryCode = (value) => {
		if (!value) return null;
		const item = Array.isArray(value) ? value[0] : value;
		const candidates = [item?.code, item?.slug, item, value].filter(
			(v) => !!v && typeof v !== 'object',
		);
		for (const candidate of candidates) {
			const normalized = String(candidate).toLowerCase();
			const found = countryEntries.find(
				([key, country]) =>
					[key, country?.code, country?.slug].some(
						(v) => String(v).toLowerCase() === normalized,
					),
			);
			if (found) return found[0].toLowerCase();
		}
		return null;
	};

	// Lowercase code ('cz', 'sk', ...) of the country whose cities are
	// shown in the right panel. `displayCode` below falls back to the
	// first country while this is empty.
	const [currentCountry, setCurrentCountry] = useState(() =>
		resolveCountryCode(activeCountry),
	);

	// Keep in sync when the prop changes (Redux / parent state update).
	// In "browse only" mode (selectCountry=false) the prop still points at the
	// previously active country, so it must NOT overwrite the user's click.
	useEffect(() => {
		if (!selectCountry) return;
		const resolved = resolveCountryCode(activeCountry);
		if (resolved) setCurrentCountry(resolved);
	}, [activeCountry, selectCountry]);

	// ── DEBUG (remove after diagnosis): a second "mounted" log right after
	// a country click means the PARENT RE-MOUNTS this component, which resets
	// currentCountry back to the initial country.
	// useEffect(() => {
	// 	console.log(
	// 		'[SelectCountry] mounted, initial country:',
	// 		resolveCountryCode(activeCountry),
	// 	);
	// 	// eslint-disable-next-line react-hooks/exhaustive-deps
	// }, []);

	// Fallback to the first available country until data / props arrive.
	const displayCode =
		currentCountry ||
		resolveCountryCode(activeCountry) ||
		transformedArray[0]?.code ||
		'';

	const countryEntry = countryEntries.find(
		([key, country]) =>
			key.toLowerCase() === displayCode ||
			String(country?.code).toLowerCase() === displayCode,
	);

	// Flag icon = code of the country currently shown.
	const flagCode = (displayCode || 'cz').toUpperCase();

	const toggleCountry = (code) => {
		const selectedCountryEntry = countryEntries.find(
			([key, country]) =>
				[key, country?.code, country?.slug].some(
					(value) =>
						String(value).toLowerCase() ===
						String(code).toLowerCase(),
				),
		);

		if (!selectedCountryEntry) return;

		const [countryCode, country] = selectedCountryEntry;
		// data items may not carry a `slug` — fall back to the code ('sk')
		const slug = country.slug || countryCode;
		if (typeof window !== 'undefined') {
			localStorage.setItem('preferredCountrySlug', slug);
		}
		const countryArray = [
			{
				id: country?.id ?? 1,
				title: country.name,
				icon: country.code.toUpperCase(),
				highlight: country?.highlight,
				slug: country.slug,
				profiles_count: country?.profiles_count ?? 0,
				sort: country.sort,
			},
		];

		if (deferApply) {
			setTitle('');
			setPaid(null);
			setSearchVal('');
			setIsOpen(true);
			setCurrentCountry(countryCode.toLowerCase());

			if (setActiveCountry) {
				setActiveCountry(countryCode);
			}

			if (onSelect) {
				onSelect({
					type: 'country',
					data: countryArray,
					code: country.code.toUpperCase(),
					slug,
				});
			}
		} else {
			dispatch(setCountrySlug(slug));
			dispatch(setCountry(countryArray));
			setCurrentCountry(countryCode.toLowerCase());
			setIsOpen(isMain ? false : true);
			dispatch(setSortParam('date'));

			if (!isMain && !changeParty) {
				setTitle('');
				setPaid(null);
				setSearchVal('');
				setValue && setValue('');
				setWholeObjectValue && setWholeObjectValue(undefined);
				setActive && setActive('');
			}

			if (setActiveCountry) {
				setActiveCountry(countryCode);
			}

			if (isMain) {
				changeCity(slug);
				dispatch(setCurrentCity(countryArray[0]));
				dispatch(setUserCurrentDistrict({}));

				const safe_params =
					url_params && url_params !== 'undefined'
						? `/${url_params
							.split('-') // разбиваем по "-"
							.map((s) => s.trim()) // чистим пробелы
							.filter((s) => s.length > 0) // убираем пустые
							.filter((s) => CityChangeSaveParams.includes(s))
							.join('-')}`
						: '';

				if (!isFilterOpen) {
					dispatch(setUserCurrentDistrict({}));
					dispatch(resetPage());
					dispatch(setAllModels([]));
					dispatch(setStories([]));
					dispatch(setCity(slug));
					dispatch(setProfileCount(country.profiles_count));
					dispatch(clearTitle());
				}
				const params =
					lang === 'en' && slug === 'czechia' && safe_params === ''
						? '/'
						: `/${lang}/${slug}` + safe_params;
				FILTER.resetFilter(false, params, true);
			}

			scrollToTop();
		}
	};

	const [title, setTitle] = useState(defaultTitle);
	const [paid, setPaid] = useState(null);
	const [searchVal, setSearchVal] = useState('');
	const [isOpen, setIsOpen] = useState(false);

	useEffect(() => {
		setIsOpen(open);
	}, [open]);

	const selectRef = useRef(null);

	// Closes the dropdown. If no city was selected yet, select the
	// first city in the list.
	const closeDropdown = (e) => {
		if (!title && searchedData?.[0]) {
			handleClick(searchedData[0], e);
		}
		setIsOpen(false);
	};

	const handleClickOutside = (event) => {
		if (selectRef.current && !selectRef.current.contains(event.target)) {
			closeDropdown(event);
			event.stopPropagation();
		}
	};

	function setValues(i, paramsOnChange, dist) {
		setTitle(`${i.name}${dist?.id ? ' ' + dist?.name : ''}`);

		setPaid(i.price);

		if (deferApply) {
			// ── Deferred: skip all dispatches, just notify parent ──
			if (onSelect) {
				onSelect({
					type: 'city',
					data: i,
					dist,
					countryCode: displayCode,
					slug: i.slug,
				});
			}
		} else {
			// ── Immediate: original dispatches ──
			dispatch(setTempCatalogueCity(false));
			dispatch(setCountrySlug(false));
		}

		if (isAgencyFilter) {
			return;
		}

		!!setValue && setValue(i.name);
		!!setWholeObjectValue && setWholeObjectValue(i);

		if (onChange) {
			if (paramsOnChange) {
				onChange(i, paramsOnChange);
			} else {
				onChange(i);
			}
		}

		if (!deferApply && (isMain || changeParty)) {
			dispatch(setSortParam('date'));
			changeCity(
				i.slug,
				i.districts.some((item) => item?.id === dist?.id) ? dist : null,
			);

			dispatch(
				setCurrentCity({
					id: i.id,
					title: i.name,
					icon: activeCountry,
					highlight: i.highlight,
					slug: i.slug,
					districts: i.districts,
				}),
			);

			if (i.districts.some((item) => item?.id === dist?.id)) {
				dispatch(setUserCurrentDistrict(dist));
			} else {
				dispatch(setUserCurrentDistrict({}));
			}

			const safe_params =
				url_params && url_params !== 'undefined'
					? `/${url_params
						.split('-') // разбиваем по "-"
						.map((s) => s.trim()) // чистим пробелы
						.filter((s) => s.length > 0) // убираем пустые
						.filter((s) => CityChangeSaveParams.includes(s))
						.join('-')}`
					: '';

			if (!isFilterOpen && !changeParty) {
				const params = `/${lang}/${i.slug}${i.districts.some((item) => item?.id === dist?.id)
					? `_${dist?.slug}` + safe_params
					: `` + safe_params
					}`;


				dispatch(resetPage());
				dispatch(setAllModels([]));
				dispatch(setStories([]));
				dispatch(setCity(i.slug));
				dispatch(setProfileCount(i.profiles_count));
				dispatch(clearTitle());
				FILTER.resetFilter(false, params, true);
			}
		}

		scrollToTop();
	}

	const handleClick = (i, e, dist = {}) => {
		e?.stopPropagation();

		if (setIsAddressActive) {
			setIsAddressActive(false);
		}
		if (setError) {
			setError('');
		}
		setValues(i, paramsOnChange, dist);
		setActive && setActive(i.id ?? 1);
		setIsOpen(false);
	};

	// Cities of the currently displayed country, filtered by search.
	const searchedData =
		!!data?.length && !!countryEntry
			? countryEntry[1]?.cities?.filter(
				(item) =>
					searchVal
						? item.name
							.toLowerCase()
							.includes(searchVal.toLowerCase())
						: item,
			) || []
			: [];

	useEffect(() => {
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [searchedData, title]);

	useEffect(() => {
		if (!searchedData || !defaultTitle) return;

		if (deferApply) {
			setTitle(defaultTitle);
		} else {
			setTitle(
				defaultTitle +
				(currentDistrict?.id ? ' ' + currentDistrict?.name : ''),
			);
		}

		const found = searchedData.find((i) => i.name === defaultTitle);
		if (found) {
			setPaid(found.price);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [defaultTitle]);

	// Pin the highlighted city first, keep the rest alphabetical.
	if (!!searchedData?.length) {
		const highlightIndex = searchedData.findIndex(
			(item) => item.highlight === 2,
		);
		if (highlightIndex !== -1) {
			const highlightedItem = searchedData.splice(highlightIndex, 1)[0];
			searchedData.sort((a, b) => a.name.localeCompare(b.name));
			searchedData.unshift(highlightedItem);
		} else {
			searchedData.sort((a, b) => a.name.localeCompare(b.name));
		}
	}

	// ── DEBUG (remove after diagnosis): what the right panel actually renders
	// console.log('[SelectCountry] render:', {
	// 	displayCode,
	// 	countryFound: !!countryEntry,
	// 	cities: searchedData.length,
	// 	firstCity: searchedData[0]?.name,
	// });

	const handleDist = (e, dist, i) => {
		e.stopPropagation();
		handleClick(i, e, dist);
	};

	const clickOpenHandler = () => {
		if (disabled) {
			return;
		}
		if (isOpen) {
			// Closing via the button uses the same fallback as outside clicks.
			closeDropdown();
		} else {
			setIsOpen(true);
		}
	};

	// selectCountry === true  → real selection: dispatch / notify parent,
	//                           keep the dropdown open, show this country's cities.
	// selectCountry === false → no side effects: just open/show this
	//                           country's cities (first one is auto-selected
	//                           on close if the user picked nothing).
	const handleCountryClick = (i) => {
		// ── DEBUG (remove after diagnosis)
		// console.log('[SelectCountry] country click:', i.code, {
		// 	selectCountry,
		// 	deferApply,
		// 	isMain,
		// });
		setSearchVal('');

		if (selectCountry) {
			toggleCountry(i.code);
		} else {
			setCurrentCountry(String(i.code).toLowerCase());
			setTitle('');
			setPaid(null);
			setIsOpen(true);
		}
	};

	return (
		<div
			className={`select-countries${required ? ' required' : ''}${isOpen ? ' select_active' : ''
				}${clazzWrapper ? ` ${clazzWrapper}` : ''}`}
			ref={selectRef}
		>
			<button
				title={paid ? `${t('adspublish')} ${t('ispaid')}` : ''}
				type={'button'}
				disabled={disabled}
				className={`${disabled ? '__disabled ' : ''}select__button${clazz ? ` ${clazz}` : ''}${clazzWrapper ? ` ${clazzWrapper}` : ''}`}
				onClick={clickOpenHandler}
			>
				{!withoutTitle && (
					<span
						className={
							`select__title` +
							(paid ? ' paid-city currency--erocoin' : '')
						}
					>
						{!withoutIcon ? (
							<Icon
								type={'flag'}
								spritePath={flagCode}
								clazz={`select__icon${clazzSvg ? ` ${clazzSvg}` : ''
									}`}
							/>
						) : null}
						<span className="text-dots">
							{title !== ''
								? title
								: (placeholder ?? t('country'))}
						</span>
					</span>
				)}

				{!withoutArrow ? (
					<Icon
						clazz={'select__arrow'}
						size={arrowSize}
						spritePath={'chevrone-down'}
					/>
				) : null}

				{error && errorMessage}
			</button>

			<div
				className={`select__body`}
				onClick={(e) => e.stopPropagation()}
			>
				<div className="select__body-left">
					{!!transformedArray?.length &&
						transformedArray.map((i) => {
							return (
								<div
									onClick={() => handleCountryClick(i)}
									className={`checkbox${displayCode === i.code.toLowerCase()
										? ' active'
										: ''
										}`}
									key={i.code}
								>
									<Icon
										type={'flag'}
										spritePath={i.code.toUpperCase()}
									/>
									<span>{i.name}<count>{` - ${i.profiles_count}`}</count></span>
								</div>
							);
						})}
				</div>

				<div className="select__body-right">
					<label
						htmlFor={`${name}-search`}
						className="select__search"
					>
						<Input
							type="search"
							placeholder={t('searchfield')}
							id={`${name}-search`}
							clazz={'search__input'}
							name={`${name}-search`}
							value={searchVal}
							setValue={setSearchVal}
						/>
					</label>

					<div className="dropdown__search-select">
						{!!data?.length && !!searchedData?.length ? (
							searchedData.map((i, index) => {
								return (
									<button
										key={index + 1}
										type={'button'}
										onClick={(e) => handleClick(i, e)}
										className={`select__item${i?.highlight === 2 ? ' fz-18' : ''
											}`}
									>
										{i.highlight === 2 ||
											i.highlight === 1 ? (
											<b
												className={
													!!i.price
														? 'paid-city currency--erocoin'
														: ''
												}
											>
												{i.title || i.name}<count>{` - ${i.profiles_count}`}</count>
											</b>
										) : (
											<span
												className={
													!!i.price
														? 'paid-city currency--erocoin'
														: ''
												}
											>
												{i.title || i.name}<count>{` - ${i.profiles_count}`}</count>
											</span>
										)}
										{/* districts select */}

										{i.districts?.length > 0 &&
											selectDistrict && (
												<Dropdown
													title={
														currentDistrict?.name ? (
															<span>{`${i.title ||
																i.name
																} ${currentDistrict?.name
																}`}</span>
														) : (
															<span>
																{t('district')}
															</span>
														)
													}
													name={t('district')}
													size={'s'}
													hideOnCHange={true}
													clazzWrapper={` district-accordion ml-auto`}
													clazz={
														' _no-arrow select_district'
													}
												// openOnHover={true}
												>
													{i.districts.map(
														(dist, idx) => {
															return (
																<span
																	style={{
																		fontSize:
																			'15px',
																	}}
																	className="hover-line"
																	key={
																		i.name +
																		idx
																	}
																	onClick={(
																		e,
																	) => {
																		handleDist(
																			e,
																			dist,
																			i,
																		);
																	}}
																>
																	{`${i.title ||
																		i.name
																		} ${dist.name}`}
																</span>
															);
														},
													)}
												</Dropdown>
											)}
									</button>
								);
							})
						) : (
							<>{t('nosearchcountry')}</>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default SelectCountry;

export const useToggleCountryCZ = () => {
	const dispatch = useDispatch();
	const countriesData = useSelector(getCountriesData);
	const changeCity = useCityChange();

	const toggleCountry = () => {
		if (countriesData[0]['cz']) {
			const country = countriesData[0]['cz'];
			const slug = country.slug;
			const countryArray = [
				{
					id: country?.id ?? 1,
					title: country.name,
					icon: country.code.toUpperCase(),
					highlight: country?.highlight,
					slug: country.slug,
					profiles_count: country?.profiles_count ?? 0,
					sort: country.sort,
				},
			];
			dispatch(setSortParam('date'));
			dispatch(setCountrySlug(slug));
			dispatch(setCountry(countryArray));
			dispatch(setCity(slug));
			changeCity(slug);
			dispatch(setCurrentCity(countryArray[0]));
			dispatch(setUserCurrentDistrict({}));
			// resetFilter(false);
		}
	};

	return toggleCountry;
};

export const useToggleCountry = () => {
	const dispatch = useDispatch();
	const countriesData = useSelector(getCountriesData);
	const changeCity = useCityChange();

	const toggleCountry = (code) => {
		if (!code || !countriesData?.length) return;

		const normalized = String(code).toLowerCase();

		// countriesData looks like [{ cz: {...} }, { sk: {...} }] (or a single
		// object with several country keys) — flatten and match case-insensitively
		// against the key, `country.code`, or `country.slug`.
		const entries = countriesData.flatMap((item) => Object.entries(item));

		const found = entries.find(([key, country]) =>
			[key, country?.code, country?.slug].some(
				(v) => v != null && String(v).toLowerCase() === normalized,
			),
		);

		if (!found) return;

		const [countryCode, country] = found;
		const slug = country?.slug || countryCode;

		const countryArray = [
			{
				id: country?.id ?? 1,
				title: country?.name,
				icon: String(country?.code ?? countryCode).toUpperCase(),
				highlight: country?.highlight,
				slug,
				profiles_count: country?.profiles_count ?? 0,
				sort: country?.sort,
			},
		];

		// Same persistence the component's toggleCountry does —
		// delete these 3 lines if you don't want it here.
		if (typeof window !== 'undefined') {
			localStorage.setItem('preferredCountrySlug', slug);
		}

		dispatch(setSortParam('date'));
		dispatch(setCountrySlug(slug));
		dispatch(setCountry(countryArray));
		dispatch(setCity(slug));
		changeCity(slug);
		dispatch(setCurrentCity(countryArray[0]));
		dispatch(setUserCurrentDistrict({}));
	};

	return toggleCountry;
};

const matchesCountry = ([code, country], value) =>
	[code, country?.code, country?.slug].some(
		(v) => v != null && String(v).toLowerCase() === value,
	);

export const useActiveCountry = () => {
	const location = useLocation();
	const lang = useSelector(getUserLang);
	const countriesData = useSelector(getCountriesData);

	const countryEntries = useMemo(
		() =>
			countriesData?.length
				? countriesData.flatMap((item) => Object.entries(item))
				: [],
		[countriesData],
	);

	return useMemo(() => {
		const build = (entry, source, city = null) => {
			if (!entry) return null;
			const [code, country] = entry;
			return {
				code,
				slug: country?.slug || code,
				country,
				city,
				source,
			};
		};

		if (!countryEntries.length) return null;

		// ── 1) URL: /en/praha, /ru/czechia/extra, /praha_district … ──
		// Every path segment is tried in order; district suffixes (`city_dist`)
		// and the lang code are ignored. First match wins.
		const segments = location.pathname
			.toLowerCase()
			.split('/')
			.filter(Boolean)
			.map((seg) => seg.split('_')[0]); // strip district part

		const meaningful =
			segments[0] === String(lang).toLowerCase()
				? segments.slice(1)
				: segments;

		for (const seg of meaningful) {
			// country slug / code / key directly in the URL
			const byCountry = countryEntries.find((e) => matchesCountry(e, seg));
			if (byCountry) return build(byCountry, 'url-country');

			// city slug in the URL → its owner country
			const byCity = countryEntries.find(([, country]) =>
				country?.cities?.some(
					(c) => String(c?.slug).toLowerCase() === seg,
				),
			);
			if (byCity) {
				const city = byCity[1].cities.find(
					(c) => String(c?.slug).toLowerCase() === seg,
				);
				return build(byCity, 'url-city', city);
			}
		}

		// ── 2) saved preference (written by toggleCountry) ──
		const saved =
			typeof window !== 'undefined'
				? localStorage.getItem('preferredCountrySlug')
				: null;
		if (saved) {
			const found = countryEntries.find((e) =>
				matchesCountry(e, saved.toLowerCase()),
			);
			if (found) return build(found, 'saved');
		}

		// ── 3) default: first country ──
		return build(countryEntries[0], 'default');
	}, [countryEntries, location.pathname, lang]);
}
